import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Gift, FlaskConical, Trophy, Share2, Download, Moon, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

function Ring({ count, target }) {
  const r = 26, c = 2 * Math.PI * r, pct = Math.min(1, count / target);
  return (
    <svg width="68" height="68" viewBox="0 0 68 68" className="shrink-0" data-testid="mission-ring">
      <circle cx="34" cy="34" r={r} fill="none" stroke="#EDE7DD" strokeWidth="7" />
      <motion.circle cx="34" cy="34" r={r} fill="none" stroke="#C86D51" strokeWidth="7" strokeLinecap="round" transform="rotate(-90 34 34)"
        strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ duration: 0.8 }} />
      <text x="34" y="39" textAnchor="middle" className="font-display" fontSize="16" fill="#2D2926">{`${count}/${target}`}</text>
    </svg>
  );
}

function Reward({ m, onChange }) {
  const open = async () => {
    try { const { data } = await api.post("/v2/missions/reward/open"); onChange(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const r = m.reward;
  if (!r.opened) return (
    <button onClick={open} data-testid="mission-reward-open" className="mt-4 w-full h-11 rounded-full bg-[#2D2926] text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
      <Gift className="h-4 w-4" />{r.title}. Open it
    </button>
  );
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-2xl bg-[#FFF8EC] p-4" data-testid="mission-reward">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-[#8A5A16]">{r.kind === "letter" ? "A letter from CAMRYN" : "Your Spark"}</p>
      <p className={`mt-1.5 text-sm text-stone-800 whitespace-pre-line ${r.kind === "letter" ? "font-display text-base" : ""}`} data-testid="mission-reward-text">{r.text}</p>
      <p className="text-[11px] text-stone-500 mt-2">A star has been added to your Year Map.</p>
    </motion.div>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => d.toISOString().slice(0, 10);

function WeekDots({ m }) {
  const start = new Date(`${m.week_start}T12:00:00Z`);
  const done = new Set(m.days || []);
  return (
    <ol className="grid grid-cols-7 gap-1 mt-4" aria-label="This week" data-testid="mission-week">
      {WEEKDAYS.map((w, i) => {
        const d = iso(new Date(start.getTime() + i * 864e5));
        const on = done.has(d), today = d === m.today, future = d > m.today;
        return (
          <li key={d} className="flex flex-col items-center gap-1" data-testid={`mission-day-${w.toLowerCase()}`} data-done={on ? "true" : "false"}>
            <span className={`text-[10px] uppercase tracking-wide ${today ? "font-bold text-stone-900" : "text-stone-500"}`}>{today ? "Today" : w}</span>
            <span className={`h-7 w-7 rounded-full grid place-items-center border-2 ${on ? "bg-[#4F6B55] border-[#4F6B55] text-white" : today ? "border-stone-900" : future ? "border-dashed border-stone-300" : "border-stone-300"}`}>
              {on && <Check className="h-3.5 w-3.5" />}</span>
          </li>);
      })}
    </ol>
  );
}

export function MissionCard({ mission, rescue, onChange }) {
  const [m, setM] = useState(mission);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  useEffect(() => setM(mission), [mission]);
  if (!m) return null;
  const act = async (path, body, msg) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try { const { data } = await api.post(path, body); setM(data); if (msg) toast.success(msg, { id: "mission-saved" }); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
    finally { setTimeout(() => { lock.current = false; setBusy(false); }, 600); }
  };
  const done = m.status === "complete";
  const state = done ? `Mission complete · ${m.count} of ${m.target} days` : m.done_today ? (m.rest_day === m.today ? "Today: rest day counted · saved" : "Today: done · saved") : "Today: not logged yet";
  return (
    <div className="card p-4 sm:p-5 scroll-mb-24 scroll-mt-20" data-testid="mission-card">
      <div className="flex items-start gap-3">
        <Ring count={m.count} target={m.target} />
        <div className="flex-1 min-w-0">
          <span className="eyebrow flex items-center gap-1.5"><Target className="h-3.5 w-3.5" />This week's mission</span>
          <p className="font-semibold text-stone-900 mt-1 leading-snug" data-testid="mission-text">{m.text}</p>
          <p className={`text-xs font-semibold mt-1 ${m.done_today || done ? "text-[#4F6B55]" : "text-stone-600"}`} data-testid="mission-today-state" aria-live="polite">{state}</p>
        </div>
      </div>
      {(!done || m.done_today) && <div className={`flex gap-2 mt-3 ${m.can_rest && rescue ? "flex-col" : "flex-col sm:flex-row lg:flex-col xl:flex-row"}`}>
        {m.done_today
          ? <button disabled={busy} onClick={() => act("/v2/missions/untick", undefined, "Today removed from this week")} data-testid="mission-untick" aria-pressed="true" className="h-12 sm:flex-none px-5 whitespace-nowrap rounded-full border-2 border-[#4F6B55] bg-[#EEF3EC] text-[#2F4A35] text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"><Check className="h-4 w-4" />{m.rest_day === m.today ? "Rest day counted" : "Done today"} · Undo</button>
          : <button disabled={busy} onClick={() => act("/v2/missions/tick", {}, "Saved for today")} data-testid="mission-tick" aria-pressed="false" className="h-12 sm:flex-none px-6 whitespace-nowrap rounded-full bg-[#392B43] text-white text-sm font-semibold disabled:opacity-60">{busy ? "Saving…" : "Mark today done"}</button>}
        {m.can_rest && rescue && <button disabled={busy} onClick={() => act("/v2/missions/tick", { rest: true }, "Rest day counted")} data-testid="mission-rest" className="h-12 px-4 whitespace-nowrap rounded-full border border-stone-200 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"><Moon className="h-4 w-4" />Count today as a rest day</button>}
      </div>}
      <WeekDots m={m} />
      <p className="text-xs text-stone-600 mt-3" data-testid="mission-line">{m.line}</p>
      {!done && m.tip && <p className="text-xs text-stone-500 mt-1">{m.tip}</p>}
      {done && m.reward && <Reward m={m} onChange={setM} />}
    </div>
  );
}

const SCORES = [1, 2, 3, 4, 5];

function DesignOwn({ onPick }) {
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const go = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/v2/experiments/design", { idea });
      if (data.unsafe) setMsg(data.message); else onPick({ id: "custom", ...data.spec, custom: data.spec });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-dashed border-stone-300 p-3 space-y-2" data-testid="experiment-design">
      <p className="text-sm font-semibold">Test your own idea</p>
      <input value={idea} onChange={(e) => setIdea(e.target.value)} maxLength={300} placeholder="e.g. Does a cold shower help my focus?" data-testid="experiment-design-input" className="w-full h-11 px-3 rounded-xl border border-stone-200 text-base" />
      <button disabled={busy || idea.trim().length < 3} onClick={go} data-testid="experiment-design-go" className="h-11 px-5 rounded-full bg-stone-900 text-white text-sm font-semibold disabled:opacity-50">{busy ? "Designing…" : "Design it with CAMRYN"}</button>
      {msg && <p className="text-xs text-[#8A5A16] bg-[#FAF4E8] rounded-xl p-3" data-testid="experiment-design-unsafe">{msg}</p>}
    </div>
  );
}

export function ExperimentSetup({ pick, onStart, onBack }) {
  const [days, setDays] = useState(4);
  const [base, setBase] = useState(null);
  return (
    <div className="mt-3 space-y-3" data-testid="experiment-setup">
      <p className="text-sm"><b>{pick.title}:</b> {pick.question}</p>
      {pick.custom && <p className="text-xs text-stone-600" data-testid="experiment-custom-detail">Each day: <b>{pick.action}</b>. Each evening: {pick.measure}</p>}
      <div><p className="text-xs text-stone-600 mb-1.5">How many days?</p>
        <div className="flex gap-2">{[3, 4, 5].map((n) => <button key={n} onClick={() => setDays(n)} data-testid={`experiment-days-${n}`} className={`h-11 w-14 rounded-full border text-sm ${days === n ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{n}</button>)}</div></div>
      <div><p className="text-xs text-stone-600 mb-1.5">Before we start: how is this usually for you? (1 = poor, 5 = great)</p>
        <div className="flex gap-2">{SCORES.map((n) => <button key={n} onClick={() => setBase(n)} data-testid={`experiment-baseline-${n}`} className={`h-11 w-11 rounded-full border text-sm ${base === n ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{n}</button>)}</div></div>
      <div className="flex gap-2">
        <button disabled={!base} onClick={() => onStart(pick, days, base)} data-testid="experiment-start" className="h-11 px-5 rounded-full bg-[#4F6B55] text-white text-sm font-semibold disabled:opacity-50">Start the experiment</button>
        {onBack && <button onClick={onBack} data-testid="experiment-back" className="h-11 px-4 text-sm text-stone-600 underline">Back</button>}
      </div>
    </div>
  );
}

export const startBody = (pick, days, baseline) => (pick.custom ? { custom: pick.custom, days, baseline } : { exp_id: pick.id, days, baseline });

function Chooser({ sugs, onStart }) {
  const [pick, setPick] = useState(null);
  if (!pick) return (
    <div className="space-y-2 mt-3" data-testid="experiment-suggestions">
      {sugs.map((x) => <button key={x.id} onClick={() => setPick(x)} data-testid={`experiment-pick-${x.id}`} className="w-full text-left rounded-2xl border border-stone-200 p-3 hover:border-stone-400 transition-colors">
        <span className="block text-sm font-semibold">{x.title}</span><span className="block text-xs text-stone-600">{x.question}</span></button>)}
      <DesignOwn onPick={setPick} />
    </div>
  );
  return <ExperimentSetup pick={pick} onStart={onStart} onBack={() => setPick(null)} />;
}

function DailyLog({ e, onLog }) {
  const [did, setDid] = useState(e.today?.did ?? null);
  return (
    <div className="mt-3 space-y-3" data-testid="experiment-log">
      <p className="text-sm">Day {Math.min(e.logged + (e.today ? 0 : 1), e.days)} of {e.days}. Did you do “{e.action.toLowerCase()}” today?</p>
      <div className="flex gap-2">
        {[[true, "Yes"], [false, "Not today"]].map(([v, l]) => <button key={l} onClick={() => setDid(v)} data-testid={`experiment-did-${v ? "yes" : "no"}`} className={`h-11 px-5 rounded-full border text-sm ${did === v ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{l}</button>)}
      </div>
      {did !== null && <div><p className="text-xs text-stone-600 mb-1.5">{e.measure} (1–5)</p>
        <div className="flex gap-2">{SCORES.map((n) => <button key={n} onClick={() => onLog(did, n)} data-testid={`experiment-score-${n}`} className={`h-11 w-11 rounded-full border text-sm ${e.today?.score === n ? "bg-[#4F6B55] text-white border-[#4F6B55]" : "border-stone-200"}`}>{n}</button>)}</div></div>}
      {e.today && <p className="text-xs text-[#4F6B55]" data-testid="experiment-logged">Logged for today. You can change it until your day ends.</p>}
    </div>
  );
}

function Result({ e }) {
  const r = e.result;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="mt-3" data-testid="experiment-result">
      <p className="font-display text-lg leading-snug" data-testid="experiment-result-text">{r.text}</p>
      {r.did_avg != null && <div className="mt-3 space-y-1.5 text-xs">
        {[["With it", r.did_avg, "#4F6B55"], [r.vs === "your usual" ? "Your usual" : "Without it", r.other_avg, "#B8AFA3"]].map(([l, v, c]) => (
          <div key={l} className="flex items-center gap-2"><span className="w-20 text-stone-600">{l}</span>
            <span className="flex-1 h-2.5 rounded-full bg-stone-100"><motion.span initial={{ width: 0 }} animate={{ width: `${v * 20}%` }} transition={{ duration: 0.8 }} className="block h-2.5 rounded-full" style={{ background: c }} /></span>
            <span className="w-8 text-right font-semibold">{v}</span></div>))}
      </div>}
    </motion.div>
  );
}

export function ExperimentCard({ goal, featured }) {
  const [x, setX] = useState(null);
  const [open, setOpen] = useState(!!featured);
  const load = useCallback(() => api.get("/v2/experiments", { params: { goal } }).then(({ data }) => setX(data)).catch(() => {}), [goal]);
  useEffect(() => { load(); }, [load]);
  if (!x) return null;
  const e = x.current;
  const run = async (fn) => { try { await fn(); await load(); } catch (err) { toast.error(errMsg(err)); } };
  const start = (pick, days, baseline) => run(() => api.post("/v2/experiments", startBody(pick, days, baseline)));
  const logIt = (did, score) => run(() => api.post(`/v2/experiments/${e.id}/log`, { did, score }));
  const reveal = async () => {
    try { const { data } = await api.post(`/v2/experiments/${e.id}/reveal`); setX((v) => ({ ...v, current: data })); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="card p-5" data-testid="experiment-card">
      <span className="eyebrow flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5" />{e ? e.title : featured ? "Experiment day" : "Personal experiments"}</span>
      {!e && !open && <button onClick={() => setOpen(true)} data-testid="experiment-open" className="tap block mt-1 py-2.5 min-h-11 text-sm text-left underline text-stone-700">Run a 3–5 day test on yourself. You're the scientist.</button>}
      {!e && open && <><p className="text-sm text-stone-700 mt-2">Pick a question. Your own data will answer it.</p><Chooser sugs={x.suggestions} onStart={start} /></>}
      {e?.status === "active" && <DailyLog e={e} onLog={logIt} />}
      {e?.status === "complete" && !e.revealed && (e.can_reveal
        ? <button onClick={reveal} data-testid="experiment-reveal" className="mt-3 h-11 px-5 rounded-full bg-[#2D2926] text-white text-sm font-semibold">Reveal my result</button>
        : <p className="mt-2 text-sm text-stone-700" data-testid="experiment-sealed">All {e.days} days logged. Your result is sealed until <b>{new Date(e.reveal_on + "T12:00").toLocaleDateString(undefined, { weekday: "long" })}</b>.</p>)}
      {e?.revealed && <Result e={e} />}
      {x.works.length > 0 && !e && <div className="mt-4 text-xs text-stone-600" data-testid="experiment-works">
        <b>Things that work for me:</b> {x.works.filter((w) => w.verdict === "works").map((w) => w.action).join(" · ") || "still testing"}</div>}
    </div>
  );
}

const W = 1080, H = 1350;
export function wrapLines(text, max) {
  return text.split(" ").reduce((lines, w) => { const l = lines[lines.length - 1]; if ((l + " " + w).length > max && l) lines.push(w); else lines[lines.length - 1] = (l + " " + w).trim(); return lines; }, [""]);
}

async function drawMilestone(cv, ms, showWeight) {
  await Promise.all(["600 110px Fraunces", "italic 400 56px Fraunces", "400 34px Figtree"].map((f) => document.fonts.load(f).catch(() => null)));
  const c = cv.getContext("2d");
  c.fillStyle = "#2D2926"; c.fillRect(0, 0, W, H);
  const g = c.createRadialGradient(W - 160, 220, 20, W - 160, 220, 760);
  g.addColorStop(0, "#C86D5188"); g.addColorStop(1, "#2D292600"); c.fillStyle = g; c.fillRect(0, 0, W, H);
  c.fillStyle = "rgba(255,255,255,0.6)"; c.font = "400 34px Figtree"; c.fillText("CAMRYN · MILESTONE", 90, 150);
  const title = ms.weight && !showWeight ? "A weight milestone" : ms.title;
  c.fillStyle = "#FBF9F5"; c.font = "600 110px Fraunces";
  title.split(" ").reduce((lines, w) => { const l = lines[lines.length - 1]; if ((l + " " + w).length > 14 && l) lines.push(w); else lines[lines.length - 1] = (l + " " + w).trim(); return lines; }, [""])
    .forEach((l, i) => c.fillText(l, 90, 520 + i * 130));
  c.font = "italic 400 56px Fraunces"; c.fillStyle = "rgba(255,255,255,0.8)"; c.fillText("Small steps, real change.", 90, 1120);
  c.font = "400 34px Figtree"; c.fillStyle = "rgba(255,255,255,0.6)"; c.fillText("mycamryn.app", 90, 1230);
}

export function ShareImage({ open, onOpenChange, draw, filename, title, testid = "milestone-share", children }) {
  const ref = useRef(null);
  useEffect(() => { if (open) setTimeout(() => ref.current && draw(ref.current), 50); }, [open, draw]);
  const blob = () => new Promise((res) => ref.current.toBlob(res, "image/png"));
  const share = async () => {
    const file = new File([await blob()], filename, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title }); } catch (e) { if (e?.name !== "AbortError") console.error(e); } return; }
    const a = document.createElement("a"); a.href = URL.createObjectURL(file); a.download = file.name; a.click();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm w-[calc(100vw-1.5rem)]" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <canvas ref={ref} width={W} height={H} className="w-full rounded-2xl" data-testid={`${testid}-canvas`} />
        {children}
        <button onClick={share} data-testid={`${testid}-btn`} className="h-11 rounded-full bg-stone-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
          {navigator.canShare ? <Share2 className="h-4 w-4" /> : <Download className="h-4 w-4" />}Share or save</button>
      </DialogContent>
    </Dialog>
  );
}

function ShareMilestone({ ms, open, onOpenChange }) {
  const [showWeight, setShowWeight] = useState(false);
  const draw = useCallback((cv) => drawMilestone(cv, ms, showWeight), [ms, showWeight]);
  return (
    <ShareImage open={open} onOpenChange={onOpenChange} draw={draw} filename="camryn-milestone.png" title="Share your milestone">
      {ms.weight && <label className="flex items-center justify-between text-sm min-h-11 cursor-pointer" data-testid="milestone-share-weight-row">Show the number<Switch checked={showWeight} onCheckedChange={setShowWeight} data-testid="milestone-share-weight-switch" /></label>}
    </ShareImage>
  );
}

export function MilestoneCard({ milestone, onSeen }) {
  const [share, setShare] = useState(false);
  if (!milestone) return null;
  const close = async () => { try { await api.post(`/v2/milestones/${milestone.key}/seen`); } catch (e) { console.error(e); } onSeen(); };
  return (
    <motion.div initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} className="card p-6 bg-[#2D2926] border-[#2D2926] text-stone-100" data-testid="milestone-card">
      <span className="eyebrow !text-[#E9B98F] flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" />Milestone</span>
      <p className="font-display text-3xl mt-2" data-testid="milestone-title">{milestone.title}</p>
      <p className="text-sm text-stone-300 mt-2">{milestone.text}</p>
      <div className="flex gap-2 mt-5">
        <button onClick={() => setShare(true)} data-testid="milestone-share-open" className="h-11 px-5 rounded-full bg-white text-stone-900 text-sm font-semibold inline-flex items-center gap-2"><Share2 className="h-4 w-4" />Share</button>
        <button onClick={close} data-testid="milestone-close" className="h-11 px-5 rounded-full border border-white/30 text-sm">Lovely, thanks</button>
      </div>
      <ShareMilestone ms={milestone} open={share} onOpenChange={setShare} />
    </motion.div>
  );
}
